/**
 * GlobalView — Zones Layer Renderer (§4.3 / §12.7)
 *
 * Renders zone polygons with glowing vertical beam walls,
 * using the same gradient band approach as the EW Layer.
 *
 * Features:
 *   - Polygon → wall beam conversion
 *   - Color coding by zone type (no_fly, danger, restricted, etc.)
 *   - Glowing translucent walls with graduated height fade
 *   - Faint ground polygon as base glow
 *   - Visibility controlled by globeStore.zonesLayerVisible
 *   - Data fetched from /api/v1/zones on toggle-on, cached
 */

import * as Cesium from 'cesium';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore } from '../../stores/entityStore';
import { apiClient } from '../api';
import type { Entity, ZoneProperties } from '@globalview/shared';

// ─── Config ──────────────────────────────────────────────────────

/** Wall beam height (meters). */
const WALL_HEIGHT = 50_000;

/** Number of gradient bands per beam. */
const BAND_COUNT = 20;

/** Base alpha for the brightest (ground-level) band */
const BASE_ALPHA = 0.30;

/** Ground polygon alpha */
const GROUND_ALPHA = 0.12;

/**
 * Generate smooth gradient bands using exponential decay.
 * Alpha follows e^(-k*t) where t is 0..1 (bottom to top).
 */
function generateGradientBands(): [number, number, number][] {
  const bands: [number, number, number][] = [];
  const decay = 3.5;

  for (let i = 0; i < BAND_COUNT; i++) {
    const fracStart = i / BAND_COUNT;
    const fracEnd = (i + 1) / BAND_COUNT;
    const midpoint = (fracStart + fracEnd) / 2;
    const alphaMult = Math.exp(-decay * midpoint);
    bands.push([fracStart, fracEnd, alphaMult]);
  }

  return bands;
}

const GRADIENT_BANDS = generateGradientBands();

// ─── Zone Colors ─────────────────────────────────────────────────

const ZONE_COLORS: Record<string, string> = {
  no_fly:     '#ff2222',
  danger:     '#ff6600',
  restricted: '#ffaa00',
  incident:   '#cc44ff',
  strike:     '#ff4466',
  custom:     '#00d4ff',
};

function getZoneColor(zoneType: string): Cesium.Color {
  const hex = ZONE_COLORS[zoneType] || '#ffaa00';
  return Cesium.Color.fromCssColorString(hex);
}

// ─── Module State ────────────────────────────────────────────────

interface ZonesLayerState {
  viewer: Cesium.Viewer;
  dataSource: Cesium.CustomDataSource;
  unsubscribeVisibility: (() => void) | null;
  loaded: boolean;
  loading: boolean;
}

let state: ZonesLayerState | null = null;

// ─── API Response ────────────────────────────────────────────────

interface ZonesApiResponse {
  zones: Entity[];
  total: number;
}

// ─── Rendering ───────────────────────────────────────────────────

/**
 * Convert GeoJSON polygon coordinates to Cesium Cartesian3 positions.
 * GeoJSON format: [[[lon, lat], [lon, lat], ...]]
 */
function polygonToPositions(polygon: number[][][]): Cesium.Cartesian3[] | null {
  if (!polygon || !polygon[0] || polygon[0].length < 3) return null;

  const ring = polygon[0];
  const positions = ring.map(([lon, lat]) =>
    Cesium.Cartesian3.fromDegrees(lon, lat),
  );

  // Ensure ring is closed
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    positions.push(Cesium.Cartesian3.fromDegrees(first[0], first[1]));
  }

  return positions;
}

/**
 * Render zone entities as glowing polygon beams with gradient fade.
 * Uses the same approach as the EW Layer: stacked wall bands with
 * exponential alpha decay from bright (ground) to transparent (top).
 */
function renderZones(zones: Entity[]): void {
  if (!state) return;

  const { dataSource } = state;
  dataSource.entities.removeAll();

  let renderedCount = 0;

  for (const zone of zones) {
    const props = zone.properties as unknown as ZoneProperties;
    if (!props.polygon) continue;

    const positions = polygonToPositions(props.polygon);
    if (!positions) continue;

    const baseColor = getZoneColor(props.zoneType || 'custom');

    // ── Gradient wall bands (bright at ground → transparent at top) ──
    for (let i = 0; i < GRADIENT_BANDS.length; i++) {
      const [fracStart, fracEnd, alphaMult] = GRADIENT_BANDS[i];
      const bandAlpha = BASE_ALPHA * alphaMult;

      const minH = new Array(positions.length).fill(WALL_HEIGHT * fracStart);
      const maxH = new Array(positions.length).fill(WALL_HEIGHT * fracEnd);

      dataSource.entities.add({
        id: `zone-beam-${zone.id}-b${i}`,
        wall: {
          positions: new Cesium.ConstantProperty(positions),
          minimumHeights: new Cesium.ConstantProperty(minH),
          maximumHeights: new Cesium.ConstantProperty(maxH),
          material: new Cesium.ColorMaterialProperty(baseColor.withAlpha(bandAlpha)),
          outline: false,
        },
        // Store properties on the first band only (for popup click detection)
        ...(i === 0 ? {
          properties: new Cesium.PropertyBag({
            entityId: zone.id,
            name: props.name || 'Unknown Zone',
            zoneType: props.zoneType || 'custom',
            description: props.description || '',
            upperAlt: props.upperAlt || 0,
            lowerAlt: props.lowerAlt || 0,
          }),
        } : {}),
      });
    }

    // ── Ground glow (faint base polygon) ──
    const groundPositions = positions.slice(0, -1); // Remove closing vertex
    dataSource.entities.add({
      id: `zone-base-${zone.id}`,
      polygon: {
        hierarchy: new Cesium.PolygonHierarchy(groundPositions),
        material: new Cesium.ColorMaterialProperty(baseColor.withAlpha(GROUND_ALPHA)),
        outline: false,
        height: new Cesium.ConstantProperty(0),
        classificationType: new Cesium.ConstantProperty(Cesium.ClassificationType.BOTH),
      },
    });

    renderedCount++;
  }

  console.log(`[ZonesLayer] Rendered ${renderedCount} zone beams (${renderedCount * (GRADIENT_BANDS.length + 1)} entities)`);
}

/**
 * Load zones from the API and render them.
 */
async function loadData(): Promise<void> {
  if (!state || state.loading) return;

  state.loading = true;
  console.log('[ZonesLayer] Loading zone data from API...');

  try {
    const response = await apiClient.get<ZonesApiResponse>('/zones');

    if (!response.zones || response.zones.length === 0) {
      console.warn('[ZonesLayer] No zone data received');
      return;
    }

    // Store in entity store
    useEntityStore.getState().upsertEntities(response.zones);

    // Render
    renderZones(response.zones);
    state.loaded = true;

    console.log(`[ZonesLayer] Loaded ${response.zones.length} zones`);
  } catch (err) {
    console.error('[ZonesLayer] Failed to load zone data:', err);
  } finally {
    if (state) state.loading = false;
  }
}

// ─── Public API ──────────────────────────────────────────────────

export function initZonesLayer(viewer: Cesium.Viewer): void {
  if (state) return;

  const dataSource = new Cesium.CustomDataSource('zones');
  viewer.dataSources.add(dataSource);

  state = {
    viewer,
    dataSource,
    unsubscribeVisibility: null,
    loaded: false,
    loading: false,
  };

  // Set initial visibility
  dataSource.show = useGlobeStore.getState().zonesLayerVisible;

  // Subscribe to visibility changes
  state.unsubscribeVisibility = useGlobeStore.subscribe((curr, prev) => {
    if (!state) return;

    if (curr.zonesLayerVisible !== prev.zonesLayerVisible) {
      state.dataSource.show = curr.zonesLayerVisible;

      // Lazy-load data when first toggled on
      if (curr.zonesLayerVisible && !state.loaded) {
        loadData();
      }
    }
  });

  // If already visible at init, load data
  if (useGlobeStore.getState().zonesLayerVisible) {
    loadData();
  }

  console.log('[ZonesLayer] Initialized');
}

export function destroyZonesLayer(): void {
  if (!state) return;

  if (state.unsubscribeVisibility) state.unsubscribeVisibility();

  state.viewer.dataSources.remove(state.dataSource, true);

  // Clear zone entities from store
  useEntityStore.getState().clearByType('zone');

  state = null;
  console.log('[ZonesLayer] Destroyed');
}
